import {
  BONUS_TYPES,
  INDIAN_STATES,
  PARTNER_TYPES,
  PERMISSIONS as P,
  campaignSchema,
  channelPartnerSchema,
  discomSchema,
  districtSchema,
  hsnSacSchema,
  itemCategorySchema,
  leadSourceSchema,
  supplierSchema,
  taxRateSchema,
  telecomClientSchema,
  uomSchema,
} from '@bsl/shared';
import { fetchOptions } from '@/composables/useOptions';
import { formatDate, formatINR } from '@/lib/format';
import type { FieldDef, MasterDef, Option } from './types';

type Row = Record<string, unknown>;

export const stateOptions: Option[] = INDIAN_STATES.map((s) => ({ value: s.code, label: s.name }));
const stateName = (code: unknown) => INDIAN_STATES.find((s) => s.code === code)?.name ?? (code as string);
const divisionOptions: Option[] = [
  { value: 'SOLAR', label: 'Solar' },
  { value: 'TELECOM', label: 'Telecom' },
];
const humanEnum = (v: string) => v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' ');
const num = (v: unknown) => (v == null || v === '' ? null : Number(v));
const isoDate = (v: unknown) => (v ? String(v).slice(0, 10) : null);

const sourceOptions = async () => (await fetchOptions<{ id: string; name: string }>('/masters/lead-sources/options')).map((s) => ({ value: s.id, label: s.name }));
const categoryOptions = async () => (await fetchOptions<{ id: string; name: string }>('/masters/item-categories/options')).map((c) => ({ value: c.id, label: c.name }));
const taxOptions = async () => (await fetchOptions<{ id: string; name: string }>('/masters/tax-rates/options')).map((t) => ({ value: t.id, label: t.name }));
const userOptions = async () => (await fetchOptions<{ id: string; fullName: string }>('/search/users')).map((u) => ({ value: u.id, label: u.fullName }));

/** Address/contact/tax fields shared by suppliers, telecom clients and channel partners. */
const partyFields = (extra: FieldDef[] = []): FieldDef[] => [
  { name: 'code', label: 'Code', type: 'text', required: true, uppercase: true },
  { name: 'name', label: 'Name', type: 'text', required: true },
  ...extra,
  { name: 'contactPerson', label: 'Contact person', type: 'text', section: 'Contact' },
  { name: 'phone', label: 'Phone', type: 'tel' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'gstin', label: 'GSTIN', type: 'text', uppercase: true, section: 'Tax and address' },
  { name: 'pan', label: 'PAN', type: 'text', uppercase: true },
  { name: 'addressLine1', label: 'Address line 1', type: 'text', wide: true },
  { name: 'addressLine2', label: 'Address line 2', type: 'text', wide: true },
  { name: 'city', label: 'City / town', type: 'text' },
  { name: 'stateCode', label: 'State', type: 'select', options: stateOptions },
  { name: 'pincode', label: 'PIN code', type: 'text' },
];
const partyColumns = [
  { key: 'code', label: 'Code', mono: true, sortable: true },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'phone', label: 'Phone', hideMobile: true },
  { key: 'city', label: 'City', hideMobile: true },
  { key: 'stateCode', label: 'State', value: (r: Row) => stateName(r.stateCode), hideMobile: true },
];
const partyDefaults = { code: '', name: '', isActive: true, stateCode: 'AP' };
const active: FieldDef = { name: 'isActive', label: 'Active', type: 'toggle' };

export const MASTER_DEFS: MasterDef[] = [
  {
    key: 'lead-sources',
    path: 'masters/lead-sources',
    title: 'Lead sources',
    singular: 'Lead source',
    description: 'Where leads come from. Used for conversion reports by source.',
    createSchema: leadSourceSchema,
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'sortOrder', label: 'Order', align: 'right' },
    ],
    fields: [{ name: 'name', label: 'Name', type: 'text', required: true }, { name: 'sortOrder', label: 'Display order', type: 'number' }, active],
    defaults: { name: '', sortOrder: 100, isActive: true },
    viewPermission: P.LEADS_VIEW,
    managePermission: P.MASTERS_MANAGE,
  },
  {
    key: 'campaigns',
    path: 'masters/campaigns',
    title: 'Campaigns',
    singular: 'Campaign',
    description: 'Marketing campaigns that leads can be attributed to.',
    createSchema: campaignSchema,
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'source', label: 'Source', value: (r) => (r.source as Row | null)?.name as string },
      { key: 'division', label: 'Division', value: (r) => (r.division ? humanEnum(r.division as string) : 'All'), hideMobile: true },
      { key: 'period', label: 'Period', value: (r) => (r.startDate ? `${formatDate(r.startDate as string)} – ${r.endDate ? formatDate(r.endDate as string) : 'open'}` : '—'), hideMobile: true },
      { key: 'budget', label: 'Budget', value: (r) => (r.budget != null ? formatINR(r.budget as string, 0) : null), align: 'right', hideMobile: true },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'sourceId', label: 'Lead source', type: 'select', options: sourceOptions },
      { name: 'division', label: 'Division', type: 'select', options: divisionOptions },
      { name: 'budget', label: 'Budget (₹)', type: 'number' },
      { name: 'startDate', label: 'Start date', type: 'date' },
      { name: 'endDate', label: 'End date', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      active,
    ],
    defaults: { name: '', isActive: true },
    toForm: (r) => ({ ...r, budget: num(r.budget), startDate: isoDate(r.startDate), endDate: isoDate(r.endDate) }),
    viewPermission: P.LEADS_VIEW,
    managePermission: P.MASTERS_MANAGE,
  },
  {
    key: 'channel-partners',
    path: 'masters/channel-partners',
    title: 'Channel partners',
    singular: 'Channel partner',
    description: 'Dealers, agents and referrers who bring in leads, with their bonus terms.',
    createSchema: channelPartnerSchema,
    columns: [
      ...partyColumns.slice(0, 2),
      { key: 'type', label: 'Type', value: (r) => humanEnum(r.type as string) },
      { key: 'bonus', label: 'Bonus', value: (r) => (r.bonusType ? `${r.bonusType === 'PERCENT' ? `${r.bonusValue}%` : r.bonusType === 'PER_KW' ? `${formatINR(r.bonusValue as string, 0)}/kW` : formatINR(r.bonusValue as string, 0)}` : null), hideMobile: true },
      { key: 'leads', label: 'Leads', value: (r) => (r._count as Row | undefined)?.leads as number, align: 'right', hideMobile: true },
      { key: 'owner', label: 'Owner', value: (r) => (r.owner as Row | null)?.fullName as string, hideMobile: true },
    ],
    fields: [
      ...partyFields([{ name: 'type', label: 'Type', type: 'select', required: true, options: PARTNER_TYPES.map((t) => ({ value: t, label: humanEnum(t) })) }]),
      { name: 'ownerId', label: 'Relationship owner', type: 'select', options: userOptions, section: 'Bonus and payment' },
      { name: 'bonusType', label: 'Bonus type', type: 'select', options: BONUS_TYPES.map((t) => ({ value: t, label: t === 'PER_KW' ? 'Per kW' : humanEnum(t) })) },
      { name: 'bonusValue', label: 'Bonus value', type: 'number', hint: 'Percent, rupees, or rupees per kW, depending on the type' },
      { name: 'bankName', label: 'Bank name', type: 'text' },
      { name: 'bankAccountNumber', label: 'Account number', type: 'text' },
      { name: 'bankIfsc', label: 'IFSC', type: 'text', uppercase: true },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      active,
    ],
    defaults: { ...partyDefaults, type: 'INDIVIDUAL' },
    toForm: (r) => ({ ...r, bonusValue: num(r.bonusValue) }),
    viewPermission: P.PARTNERS_VIEW,
    managePermission: P.PARTNERS_MANAGE,
    searchPlaceholder: 'Search code, name, phone, city',
  },
  {
    key: 'items-categories',
    path: 'masters/item-categories',
    title: 'Item categories',
    singular: 'Item category',
    description: 'Groups for items, e.g. panels, inverters, structure.',
    createSchema: itemCategorySchema,
    columns: [
      { key: 'code', label: 'Code', mono: true, sortable: true },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'parent', label: 'Parent', value: (r) => (r.parent as Row | null)?.name as string },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true, uppercase: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'parentId', label: 'Parent category', type: 'select', options: categoryOptions },
      active,
    ],
    defaults: { code: '', name: '', isActive: true },
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.ITEMS_MANAGE,
  },
  {
    key: 'uoms',
    path: 'masters/uoms',
    title: 'Units of measure',
    singular: 'Unit',
    description: 'Units used on items, BOMs and documents.',
    idField: 'code',
    createSchema: uomSchema,
    updateSchema: uomSchema.omit({ code: true }),
    columns: [
      { key: 'code', label: 'Code', mono: true, sortable: true },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'decimals', label: 'Decimals', align: 'right' },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true, uppercase: true, createOnly: true, hint: 'Cannot be changed later' },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'decimals', label: 'Decimal places allowed', type: 'number' },
      active,
    ],
    defaults: { code: '', name: '', decimals: 0, isActive: true },
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.MASTERS_MANAGE,
  },
  {
    key: 'tax-rates',
    path: 'masters/tax-rates',
    title: 'GST rates',
    singular: 'GST rate',
    description: 'Rates with effective dates and the official source they were verified against. Confirm with your tax adviser.',
    createSchema: taxRateSchema,
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'ratePercent', label: 'GST %', value: (r) => `${Number(r.ratePercent)}%`, align: 'right' },
      { key: 'effective', label: 'Effective', value: (r) => `${formatDate(r.effectiveFrom as string)} – ${r.effectiveTo ? formatDate(r.effectiveTo as string) : 'open'}` },
      { key: 'lastVerifiedAt', label: 'Last verified', value: (r) => (r.lastVerifiedAt ? formatDate(r.lastVerifiedAt as string) : 'Not verified'), hideMobile: true },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'GST 18%' },
      { name: 'ratePercent', label: 'GST rate (%)', type: 'number', required: true, hint: 'IGST = full rate; CGST and SGST = half each' },
      { name: 'cessPercent', label: 'Cess (%)', type: 'number' },
      { name: 'effectiveFrom', label: 'Effective from', type: 'date', required: true },
      { name: 'effectiveTo', label: 'Effective to', type: 'date' },
      { name: 'sourceUrl', label: 'Official source link', type: 'text', wide: true },
      { name: 'lastVerifiedAt', label: 'Last verified on', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      active,
    ],
    defaults: { name: '', ratePercent: null, cessPercent: 0, isActive: true },
    toForm: (r) => ({ ...r, ratePercent: num(r.ratePercent), cessPercent: num(r.cessPercent), effectiveFrom: isoDate(r.effectiveFrom), effectiveTo: isoDate(r.effectiveTo), lastVerifiedAt: isoDate(r.lastVerifiedAt) }),
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.TAX_MANAGE,
  },
  {
    key: 'hsn-sac',
    path: 'masters/hsn-sac',
    title: 'HSN / SAC codes',
    singular: 'HSN/SAC code',
    description: 'Goods (HSN) and services (SAC) classification codes for GST documents.',
    idField: 'code',
    createSchema: hsnSacSchema,
    updateSchema: hsnSacSchema.omit({ code: true }),
    columns: [
      { key: 'code', label: 'Code', mono: true, sortable: true },
      { key: 'description', label: 'Description', sortable: true },
      { key: 'type', label: 'Type', value: (r) => humanEnum(r.type as string) },
      { key: 'gst', label: 'Default GST', value: (r) => (r.defaultTaxRate as Row | null)?.name as string, hideMobile: true },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true, createOnly: true },
      { name: 'type', label: 'Type', type: 'select', required: true, options: [{ value: 'GOODS', label: 'Goods (HSN)' }, { value: 'SERVICES', label: 'Services (SAC)' }] },
      { name: 'description', label: 'Description', type: 'text', required: true, wide: true },
      { name: 'defaultTaxRateId', label: 'Default GST rate', type: 'select', options: taxOptions },
      active,
    ],
    defaults: { code: '', description: '', type: 'GOODS', isActive: true },
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.TAX_MANAGE,
  },
  {
    key: 'suppliers',
    path: 'masters/suppliers',
    title: 'Suppliers',
    singular: 'Supplier',
    description: 'Vendors for materials and services.',
    createSchema: supplierSchema,
    columns: partyColumns,
    fields: [
      ...partyFields(),
      { name: 'paymentTermsDays', label: 'Payment terms (days)', type: 'number', section: 'Payment' },
      { name: 'bankName', label: 'Bank name', type: 'text' },
      { name: 'bankAccountNumber', label: 'Account number', type: 'text' },
      { name: 'bankIfsc', label: 'IFSC', type: 'text', uppercase: true },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      active,
    ],
    defaults: partyDefaults,
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.SUPPLIERS_MANAGE,
    searchPlaceholder: 'Search code, name, GSTIN, phone, city',
  },
  {
    key: 'telecom-clients',
    path: 'masters/telecom-clients',
    title: 'Telecom clients',
    singular: 'Telecom client',
    description: 'Telecom operators and infrastructure companies you work for.',
    createSchema: telecomClientSchema,
    columns: partyColumns,
    fields: [
      ...partyFields(),
      { name: 'paymentTermsDays', label: 'Payment terms (days)', type: 'number', section: 'Billing' },
      { name: 'billingTerms', label: 'Billing terms', type: 'textarea' },
      active,
    ],
    defaults: partyDefaults,
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.MASTERS_MANAGE,
  },
  {
    key: 'discoms',
    path: 'masters/discoms',
    title: 'DISCOMs',
    singular: 'DISCOM',
    description: 'Electricity distribution companies, for net-metering and site records.',
    createSchema: discomSchema,
    columns: [
      { key: 'code', label: 'Code', mono: true, sortable: true },
      { key: 'name', label: 'Name', sortable: true },
      { key: 'stateCode', label: 'State', value: (r) => stateName(r.stateCode) },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true, uppercase: true },
      { name: 'name', label: 'Name', type: 'text', required: true, wide: true },
      { name: 'stateCode', label: 'State', type: 'select', required: true, options: stateOptions },
      active,
    ],
    defaults: { code: '', name: '', stateCode: 'AP', isActive: true },
    filters: [{ name: 'stateCode', label: 'States', options: stateOptions }],
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.MASTERS_MANAGE,
  },
  {
    key: 'districts',
    path: 'masters/districts',
    title: 'Districts',
    singular: 'District',
    description: 'District list per state, used on leads, customers and sites.',
    createSchema: districtSchema,
    columns: [
      { key: 'name', label: 'District', sortable: true },
      { key: 'stateCode', label: 'State', value: (r) => stateName(r.stateCode) },
    ],
    fields: [{ name: 'stateCode', label: 'State', type: 'select', required: true, options: stateOptions }, { name: 'name', label: 'District name', type: 'text', required: true }, active],
    defaults: { stateCode: 'AP', name: '', isActive: true },
    filters: [{ name: 'stateCode', label: 'States', options: stateOptions }],
    viewPermission: P.MASTERS_VIEW,
    managePermission: P.MASTERS_MANAGE,
  },
];
